import type { TriageSourceImage, TriageSourceImageView } from '@elva/ui';
import { useEffect, useState } from 'react';
import { getBlob } from '@/api/client';

const DIRECT_URL_RE = /^(https?:|data:|blob:)/i;

type ImageState = Pick<TriageSourceImageView, 'url' | 'loading' | 'error'>;

/**
 * Resolves each card image to something an <img> can show. Relative API paths
 * (`/api/triage/{id}/attachments/{id}`) need the JWT, so they're fetched as blobs;
 * absolute URLs (legacy external signed links) are used as-is.
 */
export function useTriageSourceImages(
  sources: TriageSourceImage[] | undefined,
  enabled: boolean,
): TriageSourceImageView[] {
  const [states, setStates] = useState<Record<string, ImageState>>({});
  const signature = enabled && sources ? sources.map((s) => `${s.key}|${s.url}`).join('\n') : '';

  useEffect(() => {
    if (!enabled || !sources || sources.length === 0) {
      setStates({});
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const objectUrls: string[] = [];
    const initial: Record<string, ImageState> = {};
    for (const s of sources) {
      initial[s.key] = DIRECT_URL_RE.test(s.url)
        ? { url: s.url, loading: false, error: false }
        : { url: null, loading: true, error: false };
    }
    setStates(initial);

    for (const s of sources) {
      if (DIRECT_URL_RE.test(s.url)) continue;
      getBlob(s.url, undefined, { signal: controller.signal })
        .then((blob) => {
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          objectUrls.push(url);
          setStates((prev) => ({ ...prev, [s.key]: { url, loading: false, error: false } }));
        })
        .catch(() => {
          if (!cancelled) setStates((prev) => ({ ...prev, [s.key]: { url: null, loading: false, error: true } }));
        });
    }

    return () => {
      cancelled = true;
      controller.abort();
      objectUrls.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `signature` captures sources' identity
  }, [signature, enabled]);

  return (sources ?? []).map((s) => ({
    key: s.key,
    label: s.label,
    url: states[s.key]?.url ?? null,
    loading: states[s.key]?.loading ?? enabled,
    error: states[s.key]?.error ?? false,
  }));
}
